import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, useMediaQuery, CircularProgress } from '@mui/material';
import Carousel from 'react-material-ui-carousel';
import { useTheme } from '@mui/material/styles';

// Fallback banner items in case fetching fails
const defaultBannerItems = [
  {
    id: 1,
    image: '/images/banner1.svg',
    title: 'Ranko Agricultural Nutrient',
    description: 'Application Guide for Premium Crop Fertilizers'
  },
  {
    id: 2,
    image: '/images/banner2.svg',
    title: '15-15-30 Formula',
    description: 'Premium Nutrient for High Yield Crop Production'
  },
  {
    id: 3,
    image: '/images/banner3.svg',
    title: 'Syarikat Ranko Sdn. Bhd.',
    description: 'Professional Agricultural Nutrient Solutions'
  }
];

const BannerSlider = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [bannerItems, setBannerItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch('/images/banner.json');
        if (!response.ok) {
          throw new Error('Failed to fetch banner data');
        }
        const data = await response.json();
        setBannerItems(data.banners);
      } catch (error) {
        console.error('Error loading banner data:', error);
        setBannerItems(defaultBannerItems);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBanners();
  }, []);
  
  if (loading) {
    return (
      <Box 
        sx={{
          width: '100%',
          height: isMobile ? '200px' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.05)',
          borderRadius: 2,
          mb: 4
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box 
      sx={{
        width: '100%',
        mb: 4,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }}
    >
      <Carousel
        autoPlay={true}
        animation="fade"
        indicators={true}
        navButtonsAlwaysVisible={!isMobile}
        navButtonsWrapperProps={{
          style: {
            bottom: 0,
            top: 'unset',
            padding: '0 8px'
          }
        }}
        indicatorContainerProps={{
          style: {
            position: 'absolute',
            bottom: '8px',
            zIndex: 2
          }
        }}
        indicatorIconButtonProps={{
          style: {
            color: 'rgba(255, 255, 255, 0.5)',
            margin: '0 4px'
          }
        }}
        activeIndicatorIconButtonProps={{
          style: {
            color: '#ffffff'
          }
        }}
        navButtonsProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: '#ffffff'
          }
        }}
        interval={6000}
        className="banner-slider"
      >
        {bannerItems.map((item) => (
          <BannerItem key={item.id} item={item} isMobile={isMobile} />
        ))}
      </Carousel>
    </Box>
  );
};

const BannerItem = ({ item, isMobile }) => {
  return (
    <Paper
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        height: isMobile ? '200px' : '400px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end'
      }}
    >
      <img 
        src={item.image} 
        alt={item.title}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
          color: 'white',
          textShadow: '0px 1px 2px rgba(0,0,0,0.8)'
        }}
      >
        <Typography variant={isMobile ? 'h6' : 'h5'} component="h2" fontWeight="bold">
          {item.title}
        </Typography>
        <Typography variant={isMobile ? 'body2' : 'body1'}>
          {item.description}
        </Typography>
      </Box>
    </Paper>
  );
};

export default BannerSlider; 